import { useMemo, useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import {
  useDocuments,
  type GeneratedDocumentOut,
} from '@/features/emr/api/documents-api';
import { useUploadedDocuments } from '@/features/emr/api/uploaded-documents-api';
import type { UploadedDocumentOut } from '@/features/emr/types';
import {
  InlineDocumentViewer,
  type InlineDocumentItem,
} from './InlineDocumentViewer';

interface DocumentsTabProps {
  patientId: string;
  filterType?: string;
}

function toUnifiedItem(doc: GeneratedDocumentOut): InlineDocumentItem {
  return {
    id: doc.id,
    name: doc.template?.name ?? 'Generated Document',
    type: doc.status,
    date: doc.generated_at,
    content: doc.content,
    source: 'generated',
  };
}

function toUnifiedItemFromUploaded(doc: UploadedDocumentOut): InlineDocumentItem {
  return {
    id: doc.id,
    name: doc.name,
    type: doc.document_type,
    date: doc.document_date,
    content: doc.current_revision?.extracted_text ?? null,
    source: 'uploaded',
  };
}

export function DocumentsTab({ patientId, filterType }: DocumentsTabProps) {
  const [selectedDoc, setSelectedDoc] = useState<InlineDocumentItem | null>(null);

  const { data: generatedData, isLoading: loadingGenerated } = useDocuments({
    patient_id: patientId,
    limit: 100,
  });

  const { data: uploadedData, isLoading: loadingUploaded } =
    useUploadedDocuments({
      patient_id: patientId,
      limit: 100,
    });

  const isLoading = loadingGenerated || loadingUploaded;

  const items = useMemo(() => {
    const generated = (generatedData?.data ?? []).map(toUnifiedItem);
    const uploaded = (uploadedData?.data ?? []).map(toUnifiedItemFromUploaded);
    let merged = [...generated, ...uploaded];

    if (filterType) {
      merged = merged.filter((item) => item.type === filterType);
    }

    merged.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
    return merged;
  }, [generatedData, uploadedData, filterType]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Inline document viewer
  if (selectedDoc) {
    return (
      <InlineDocumentViewer
        item={selectedDoc}
        onBack={() => setSelectedDoc(null)}
      />
    );
  }

  // Document list
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <FileText className="mb-2 h-8 w-8" />
        <p className="text-sm">
          {filterType ? `No ${filterType} documents found.` : 'No documents found.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {items.map((item) => (
        <button
          key={`${item.source}-${item.id}`}
          onClick={() => setSelectedDoc(item)}
          className={cn(
            'flex w-full items-start gap-3 rounded-md border p-2.5 text-left transition-colors',
            'hover:bg-muted/50',
          )}
        >
          <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{item.name}</p>
            <div className="mt-0.5 flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {new Date(item.date).toLocaleDateString()}
              </span>
              <Badge variant="outline" className="text-[10px]">
                {item.type}
              </Badge>
              <Badge
                variant="secondary"
                className="text-[10px]"
              >
                {item.source}
              </Badge>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}
